/**
 * Iteration 31 — Bilingual translations for the Attendance Hub.
 *
 * Single source of truth so every tab + the check-in screen + the PDF
 * export share the exact same labels. Lookup uses `attT(lang, "key")`.
 *
 * Keys are dot-namespaced by surface so they don't collide with the
 * global TRANSLATIONS map in /app/frontend/src/lib/i18n.js.
 */
export const ATTENDANCE_I18N = {
  ar: {
    // ── Page chrome ─────────────────────────────────────────
    "title": "الحضور والانصراف",
    "back": "← رجوع",
    "back_ltr": "Back →",
    "stas_only": "هذه الصفحة لـ STAS والمدراء والمحاسب فقط.",
    "loading": "جاري التحميل…",
    "saving": "جاري الحفظ…",
    "save": "حفظ",
    "cancel": "إلغاء",
    "delete": "حذف",
    "confirm_delete": "تأكيد حذف الإجازة؟",
    "saved_ok": "تم الحفظ بنجاح.",
    "view_only": "عرض فقط",

    // ── Tabs ────────────────────────────────────────────────
    "tab.timesheet": "الجدول الشهري",
    "tab.leaves": "الإجازات",
    "tab.rules": "قواعد العمل",
    "tab.geofence": "موقع المكتب",

    // ── Timesheet grid ─────────────────────────────────────
    "ts.search_ph": "🔍 بحث باسم الموظف…",
    "ts.col.employee": "الموظف",
    "ts.col.shift": "الشفت",
    "ts.col.present": "الحضور",
    "ts.col.absent": "الغياب",
    "ts.col.late": "المتأخر",
    "ts.shift.morning": "صباحي",
    "ts.shift.evening": "مسائي",
    "ts.export_pdf": "📄 تصدير PDF",
    "ts.empty": "لا يوجد موظفون بعد، أو لا نتائج لبحثك.",
    "ts.legend.ramadan": "🌙 رمضان",

    // KPI cards + custom date range
    "ts.kpi.title": "ملخص الفترة",
    "ts.kpi.hours_total": "إجمالي الساعات",
    "ts.kpi.days_present": "أيام الحضور",
    "ts.kpi.days_absent": "أيام الغياب",
    "ts.kpi.late_count": "مرات التأخر",
    "ts.kpi.late_minutes": "إجمالي دقائق التأخر",
    "ts.kpi.hours": "س",
    "ts.kpi.minutes": "د",
    "ts.kpi.day": "يوم",
    "ts.kpi.time": "مرة",
    "ts.range.month_mode": "حسب الشهر",
    "ts.range.custom_mode": "نطاق مخصص",
    "ts.range.from": "من",
    "ts.range.to": "إلى",
    "ts.range.apply": "تطبيق",
    "ts.cell.in": "دخول",
    "ts.cell.out": "خروج",

    // ── Status labels ──────────────────────────────────────
    "status.present": "حاضر",
    "status.half": "نصف دوام",
    "status.absent": "غائب",
    "status.excused": "بعذر",
    "status.approved_leave": "إجازة",
    "status.permitted": "مأذون",
    "status.sick": "مرضي",
    "status.off": "إجازة رسمية",
    "status.unrecorded": "لم يُسجّل",

    // ── Day-of-week short labels (Sun-Sat) ────────────────
    "dow.short": ["أحد", "اثن", "ثلا", "أرب", "خمي", "جمع", "سبت"],

    // ── Months ─────────────────────────────────────────────
    "months": ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"],

    // ── Edit-cell modal ────────────────────────────────────
    "ts.edit.title": "تعديل اليوم — {name} ({day})",
    "ts.edit.status": "الحالة",
    "ts.edit.note": "ملاحظة",
    "ts.edit.note_ph": "مثلاً: حضر بعد العاشرة",
    "ts.late_minutes": "متأخر {min} د",

    // ── Leave-periods tab ──────────────────────────────────
    "leave.count": "عدد الإجازات",
    "leave.add_btn": "+ إضافة إجازة",
    "leave.form.employee": "الموظف",
    "leave.form.select_ph": "— اختر —",
    "leave.form.from": "من تاريخ",
    "leave.form.to": "إلى تاريخ",
    "leave.form.type": "النوع",
    "leave.form.reason": "السبب (اختياري)",
    "leave.form.reason_ph": "مثلاً: سفر عائلي",
    "leave.form.submit": "حفظ الإجازة",
    "leave.empty": "لا توجد إجازات مسجلة بعد.",
    "leave.reason_label": "السبب",
    "leave.range": "من {from} إلى {to}",
    "leave.type.approved_leave": "إجازة معتمدة",
    "leave.type.excused": "غياب بعذر",
    "leave.type.sick": "إجازة مرضية",
    "leave.type.permitted": "مأذون",

    // ── Work-rules tab ─────────────────────────────────────
    "rules.section.general": "القواعد العامة",
    "rules.section.normal": "أوقات الدوام العادي",
    "rules.section.ramadan": "🌙 وضع رمضان (ساعات مرنة)",
    "rules.friday_off": "الجمعة إجازة رسمية",
    "rules.sat_half": "السبت نصف دوام",
    "rules.late_tolerance": "مدة سماح التأخير (دقائق)",
    "rules.morning": "الشفت الصباحي",
    "rules.evening": "الشفت المسائي",
    "rules.saturday": "السبت (نصف دوام)",
    "rules.ramadan_active": "تفعيل وضع رمضان",
    "rules.ramadan_from": "بداية رمضان",
    "rules.ramadan_to": "نهاية رمضان",
    "rules.ramadan_morning": "الصباحي (رمضان)",
    "rules.ramadan_evening": "المسائي (رمضان)",
    "rules.ramadan_saturday": "السبت (رمضان)",
    "rules.save": "حفظ القواعد",

    // ── Geofence tab ───────────────────────────────────────
    "geo.info_can_edit": "اضغط على الخريطة لتحديد موقع المكتب. الموظف لا يستطيع تسجيل الحضور إلا من داخل الدائرة.",
    "geo.info_readonly": "عرض فقط — تعديل موقع المكتب متاح لـ STAS.",
    "geo.lat": "خط العرض (Lat)",
    "geo.lng": "خط الطول (Lng)",
    "geo.radius": "نصف القطر (5-20 م)",
    "geo.accuracy": "أعلى دقة GPS مسموحة (م) — يُنصح 100-200",
    "geo.use_my_loc": "استخدم موقعي الحالي",
    "geo.save": "حفظ الإعدادات",
    "geo.current": "الموقع الحالي: {lat}, {lng} — شعاع {r} م",
    "geo.gps_unsupported": "المتصفح لا يدعم تحديد الموقع",
    "geo.gps_fail": "فشل تحديد الموقع: {err}",

    // ── Check-in screen (employee) ─────────────────────────
    "ci.employee": "الموظف",
    "ci.date": "التاريخ",
    "ci.in": "الحضور",
    "ci.out": "الانصراف",
    "ci.btn.in": "تسجيل الحضور",
    "ci.btn.in_done": "تم تسجيل الحضور ✓",
    "ci.btn.out": "تسجيل الانصراف",
    "ci.btn.out_done": "تم تسجيل الانصراف ✓",
    "ci.btn.locating": "جاري التحقق من الموقع…",
    "ci.ok_in": "تم تسجيل الحضور ✓ — المسافة من المكتب: {m} م",
    "ci.ok_out": "تم تسجيل الانصراف ✓ — المسافة من المكتب: {m} م",
    "ci.gps_unsupported": "المتصفح لا يدعم GPS.",
    "ci.gps_fail": "فشل الحصول على الموقع: {err}",
    "ci.note": "ⓘ يجب أن تكون داخل دائرة المكتب المحددة من قِبَل STAS.",
    "ci.hint": "💡 نصيحة لدقة GPS أفضل: اقترب من النافذة أو اخرج لمدخل المكتب قبل الضغط، وامنح المتصفح إذن الوصول إلى الموقع.",

    // ── User Features page ─────────────────────────────────
    "uf.title": "إدارة أيقونات الواجهة",
    "uf.user_label": "المستخدم",
    "uf.info": "ضع علامة على كل ميزة تريد إخفاءها من هذا المستخدم. STAS لا يتأثر بهذه القيود إطلاقاً.",
    "uf.hidden_count": "المخفي حالياً: {n} ميزة.",
    "uf.tag.hidden": "مخفي",
    "uf.tag.visible": "ظاهر",
    "uf.save_ok": "تم الحفظ بنجاح. سيتم تطبيق التغييرات على المستخدم فوراً.",
    "uf.save": "حفظ التغييرات",
    "uf.reset": "إلغاء كل الإخفاءات",
    "uf.stas_only": "هذه الصفحة لـ STAS فقط.",
  },

  en: {
    // ── Page chrome ─────────────────────────────────────────
    "title": "Attendance Hub",
    "back": "← Back",
    "back_ltr": "Back →",
    "stas_only": "This page is for STAS, managers, and the accountant only.",
    "loading": "Loading…",
    "saving": "Saving…",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "confirm_delete": "Confirm leave deletion?",
    "saved_ok": "Saved successfully.",
    "view_only": "View only",

    // ── Tabs ────────────────────────────────────────────────
    "tab.timesheet": "Monthly Timesheet",
    "tab.leaves": "Leaves",
    "tab.rules": "Work Rules",
    "tab.geofence": "Office Location",

    // ── Timesheet grid ─────────────────────────────────────
    "ts.search_ph": "🔍 Search by employee name…",
    "ts.col.employee": "Employee",
    "ts.col.shift": "Shift",
    "ts.col.present": "Present",
    "ts.col.absent": "Absent",
    "ts.col.late": "Late",
    "ts.shift.morning": "Morning",
    "ts.shift.evening": "Evening",
    "ts.export_pdf": "📄 Export PDF",
    "ts.empty": "No employees yet, or no results for your search.",
    "ts.legend.ramadan": "🌙 Ramadan",

    // KPI cards + custom date range
    "ts.kpi.title": "Period summary",
    "ts.kpi.hours_total": "Total hours",
    "ts.kpi.days_present": "Days present",
    "ts.kpi.days_absent": "Days absent",
    "ts.kpi.late_count": "Late incidents",
    "ts.kpi.late_minutes": "Total late minutes",
    "ts.kpi.hours": "h",
    "ts.kpi.minutes": "m",
    "ts.kpi.day": "day",
    "ts.kpi.time": "time",
    "ts.range.month_mode": "By month",
    "ts.range.custom_mode": "Custom range",
    "ts.range.from": "From",
    "ts.range.to": "To",
    "ts.range.apply": "Apply",
    "ts.cell.in": "In",
    "ts.cell.out": "Out",

    // ── Status labels ──────────────────────────────────────
    "status.present": "Present",
    "status.half": "Half day",
    "status.absent": "Absent",
    "status.excused": "Excused",
    "status.approved_leave": "Leave",
    "status.permitted": "Permitted",
    "status.sick": "Sick",
    "status.off": "Off (Friday)",
    "status.unrecorded": "Unrecorded",

    // ── Day-of-week short labels (Sun-Sat) ────────────────
    "dow.short": ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],

    // ── Months ─────────────────────────────────────────────
    "months": ["January","February","March","April","May","June","July","August","September","October","November","December"],

    // ── Edit-cell modal ────────────────────────────────────
    "ts.edit.title": "Edit day — {name} ({day})",
    "ts.edit.status": "Status",
    "ts.edit.note": "Note",
    "ts.edit.note_ph": "e.g. Arrived after 10 AM",
    "ts.late_minutes": "{min} min late",

    // ── Leave-periods tab ──────────────────────────────────
    "leave.count": "Leave entries",
    "leave.add_btn": "+ Add leave",
    "leave.form.employee": "Employee",
    "leave.form.select_ph": "— Select —",
    "leave.form.from": "From date",
    "leave.form.to": "To date",
    "leave.form.type": "Type",
    "leave.form.reason": "Reason (optional)",
    "leave.form.reason_ph": "e.g. Family trip",
    "leave.form.submit": "Save leave",
    "leave.empty": "No leave entries yet.",
    "leave.reason_label": "Reason",
    "leave.range": "From {from} to {to}",
    "leave.type.approved_leave": "Approved Leave",
    "leave.type.excused": "Excused Absence",
    "leave.type.sick": "Sick Leave",
    "leave.type.permitted": "Permitted",

    // ── Work-rules tab ─────────────────────────────────────
    "rules.section.general": "General Rules",
    "rules.section.normal": "Regular Working Hours",
    "rules.section.ramadan": "🌙 Ramadan Mode (Flexible hours)",
    "rules.friday_off": "Friday is an official day off",
    "rules.sat_half": "Saturday is a half day",
    "rules.late_tolerance": "Late tolerance (minutes)",
    "rules.morning": "Morning shift",
    "rules.evening": "Evening shift",
    "rules.saturday": "Saturday (half day)",
    "rules.ramadan_active": "Activate Ramadan mode",
    "rules.ramadan_from": "Ramadan starts",
    "rules.ramadan_to": "Ramadan ends",
    "rules.ramadan_morning": "Morning (Ramadan)",
    "rules.ramadan_evening": "Evening (Ramadan)",
    "rules.ramadan_saturday": "Saturday (Ramadan)",
    "rules.save": "Save rules",

    // ── Geofence tab ───────────────────────────────────────
    "geo.info_can_edit": "Click on the map to set the office location. Employees cannot check in unless they are inside the circle.",
    "geo.info_readonly": "View only — only STAS can modify the office location.",
    "geo.lat": "Latitude",
    "geo.lng": "Longitude",
    "geo.radius": "Radius (5-20 m)",
    "geo.accuracy": "Max GPS accuracy allowed (m) — recommended 100-200",
    "geo.use_my_loc": "Use my current location",
    "geo.save": "Save settings",
    "geo.current": "Current location: {lat}, {lng} — radius {r} m",
    "geo.gps_unsupported": "Your browser does not support geolocation",
    "geo.gps_fail": "Failed to get location: {err}",

    // ── Check-in screen (employee) ─────────────────────────
    "ci.employee": "Employee",
    "ci.date": "Date",
    "ci.in": "Check-in",
    "ci.out": "Check-out",
    "ci.btn.in": "Check in",
    "ci.btn.in_done": "Checked in ✓",
    "ci.btn.out": "Check out",
    "ci.btn.out_done": "Checked out ✓",
    "ci.btn.locating": "Verifying location…",
    "ci.ok_in": "Checked in ✓ — distance from office: {m} m",
    "ci.ok_out": "Checked out ✓ — distance from office: {m} m",
    "ci.gps_unsupported": "Your browser does not support GPS.",
    "ci.gps_fail": "Failed to get location: {err}",
    "ci.note": "ⓘ You must be inside the office circle defined by STAS.",
    "ci.hint": "💡 For better GPS accuracy: move closer to a window or step outside the office entrance before pressing, and grant browser location access.",

    // ── User Features page ─────────────────────────────────
    "uf.title": "UI Feature Visibility",
    "uf.user_label": "User",
    "uf.info": "Tick every feature you want to hide from this user. STAS is never affected by these restrictions.",
    "uf.hidden_count": "Currently hidden: {n} feature(s).",
    "uf.tag.hidden": "Hidden",
    "uf.tag.visible": "Visible",
    "uf.save_ok": "Saved. Changes apply to the user immediately.",
    "uf.save": "Save changes",
    "uf.reset": "Clear all hides",
    "uf.stas_only": "This page is for STAS only.",
  },
};

/** Helper — resolve a translation with optional {var} interpolation. */
export function attT(lang, key, vars) {
  const dict = ATTENDANCE_I18N[lang] || ATTENDANCE_I18N.ar;
  let raw = dict[key];
  if (raw == null) raw = ATTENDANCE_I18N.ar[key] ?? key;
  if (Array.isArray(raw) || typeof raw !== "string") return raw;
  if (vars) {
    for (const k of Object.keys(vars)) {
      raw = raw.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]);
    }
  }
  return raw;
}
